import { useShellSearchParams } from '../lib/use-shell-search-params';
import { LocaleLink as Link } from '../i18n/locale-link';
import { useLocale } from '../i18n/locale-context';
import { useSearchFacets } from '../lib/use-search-facets';
import { countryConfig } from '../country';
import type { PublicCountryConfig } from '@tenders/shared';

type Scope = 'all' | 'ifi' | 'supply';

const CHIPS: Array<{ scope: Scope; to: string; labelKey: string }> = [
  { scope: 'all', to: '/catalog', labelKey: 'shell.nav.allTenders' },
  { scope: 'ifi', to: '/ifi', labelKey: 'shell.nav.ifiProjects' },
  { scope: 'supply', to: '/supply', labelKey: 'shell.nav.supplyRequests' },
];

/**
 * The chips this deployment can fill. «Кооперация» is cooperation.uz supply
 * requests: a deployment with no supply-scope source (KZ) drops the chip from
 * its CONFIG, not from a zero count — a zero is a real answer on UZ («this
 * query has no supply requests»), but on KZ it could never be anything else
 * (phone QA, 2026-10-16: «Все тендеры 149 · Проекты МФИ 3 · Кооперация 0»).
 */
export function visibleScopeChips(
  config: Pick<PublicCountryConfig, 'features'> = countryConfig,
): typeof CHIPS {
  return config.features.supplyScope ? CHIPS : CHIPS.filter((c) => c.scope !== 'supply');
}

/**
 * Scope filter chips above the search results — «Все тендеры · Проекты МФИ ·
 * Запросы предложений» with the QUERY's per-layer counts, each a link that
 * re-scopes while carrying the query (`/ifi?q=…`). The catalog default browses
 * gov+corporate only, so a search is where a lot's layer actually varies; this
 * is the surface to see and act on that split.
 *
 * Only rendered while a search is active (the facet hook is disabled otherwise
 * and the counts have no meaning off a query — browse scope is the sidebar's
 * job). Counts share ONE request with the kind tabs via `useSearchFacets`.
 * A chip whose layer has zero matches is dimmed but kept, so the row's shape is
 * stable and «this query has no МФИ lots» is itself an answer.
 *
 * No numbers at all while a filter the counts do not apply is set (`uncounted`
 * — the budget bounds, the platform): the list below is narrowed by it and the
 * chips are not, so «Все тендеры 25» stood above «7 из 47 297 тендеров».
 */
export function ScopeChips({ activeScope }: { activeScope: Scope }) {
  const { t } = useLocale();
  const [params] = useShellSearchParams();
  const { facets, active, uncounted } = useSearchFacets();
  const q = params.get('q')?.trim() ?? '';
  if (!active) return null;

  return (
    <nav
      aria-label={t('scope.chips.aria')}
      className="-mx-1 my-3 flex items-center gap-2 overflow-x-auto px-1 pb-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    >
      {visibleScopeChips().map(({ scope, to, labelKey }) => {
        const count = uncounted ? undefined : facets?.scope[scope];
        const isActive = scope === activeScope;
        const empty = count === 0;
        return (
          <Link
            key={scope}
            to={`${to}?q=${encodeURIComponent(q)}`}
            aria-current={isActive ? 'page' : undefined}
            className={`inline-flex shrink-0 items-center gap-1.5 rounded-pill border px-3 py-1 text-[12.5px] font-semibold transition-colors ${
              isActive
                ? 'border-brand/40 bg-brand-dim text-brand'
                : `border-edge bg-bone-surface text-ink-soft hover:border-brand/30 hover:text-ink ${
                    empty ? 'opacity-55' : ''
                  }`
            }`}
          >
            <span>{t(labelKey)}</span>
            {count != null && (
              <span
                className={`min-w-[18px] rounded-pill px-1.5 text-center text-[11px] font-bold leading-[1.5] ${
                  isActive ? 'bg-brand/15 text-brand' : 'bg-bone text-ink-muted'
                }`}
              >
                {count}
              </span>
            )}
          </Link>
        );
      })}
    </nav>
  );
}
